import { useState, ReactNode, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  MapPin,
  User,
  CheckCircle2,
  Clock,
  FileCheck,
  FileEdit,
  AlertTriangle,
  Scale,
  Users as UsersIcon,
  Laptop,
  Sparkles,
  TrendingUp,
  Search,
  X,
  FileText,
  Calendar,
  Hash,
  Inbox,
  Loader2,
} from "lucide-react";
import { api } from "@/api/client";
import { STATUS_COLORS, type DocumentStatus } from "@/types/document";
import PublicHeader from "@/components/common/PublicHeader";

// ── Types from public API ────────────────────────────────────────────────────
type Stakeholder = {
  id: string;
  nama: string;
  tipe: string;
  deskripsi: string | null;
};

type Dimensi = {
  id: string;
  kode: string;
  nama: string;
  deskripsi: string | null;
  urutan: number;
};

type DokumenRowApi = {
  id: string;
  judul: string;
  nomor_dokumen: string | null;
  deskripsi: string | null;
  kategori: string | null;
  status: string | null;
  tags: string[];
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  ai_summary: string | null;
  tanggal_dokumen: string | null;
};

// Display order + label override for dimensi tabs (keyed by m_dimensi.kode).
// Lets the UI render the agreed taxonomy regardless of legacy DB values.
const DIMENSI_DISPLAY: Record<string, { order: number; nama: string }> = {
  D02: { order: 1, nama: "Organisasi" },
  D01: { order: 2, nama: "Regulasi" },
  D04: { order: 3, nama: "Pedoman Operasional" },
  D03: { order: 4, nama: "Rekomendasi Kebijakan" },
  D05: { order: 5, nama: "Data dan Statistik" },
  D06: { order: 6, nama: "Sistem Informasi" },
  D07: { order: 7, nama: "Knowledge Sharing" },
};

// Status pill — same style as the admin DocumentListPage so behavior is consistent
// across the system. Color comes from `STATUS_COLORS` (keyed by m_status_dokumen.kode).
function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  const kode = status.toUpperCase() as DocumentStatus;
  const colorClass = STATUS_COLORS[kode] ?? "bg-gray-100 text-gray-700";
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}
    >
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Landing page for PNBP — data driven via dropdowns
// ─────────────────────────────────────────────────────────────────────────────

type DokumenRow = {
  kategori: string;
  icon: ReactNode;
  chip: string;
  nama: string;
  status: string;
  statusIcon: ReactNode;
  atribut: string;
};

type Insight = { icon: ReactNode; title: string; body: ReactNode };

type Dataset = {
  title: string;
  anchorLaw: string;
  picUtama: string;
  picRole: string;
  progress: number;
  rows: DokumenRow[];
  insights: Insight[];
};

const ROW_REGULASI = (
  nama: string,
  status: string,
  atribut: string,
): DokumenRow => ({
  kategori: "REGULASI",
  icon: <Scale className="h-4 w-4 text-amber-600" />,
  chip: "bg-amber-50 text-amber-700",
  nama,
  status,
  statusIcon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  atribut,
});
const ROW_PROSES = (
  nama: string,
  status: string,
  atribut: string,
): DokumenRow => ({
  kategori: "PROSES",
  icon: <FileEdit className="h-4 w-4 text-sky-600" />,
  chip: "bg-sky-50 text-sky-700",
  nama,
  status,
  statusIcon: <Clock className="h-4 w-4 text-amber-500" />,
  atribut,
});
const ROW_TATA = (
  nama: string,
  status: string,
  atribut: string,
): DokumenRow => ({
  kategori: "TATA KELOLA",
  icon: <UsersIcon className="h-4 w-4 text-violet-600" />,
  chip: "bg-violet-50 text-violet-700",
  nama,
  status,
  statusIcon: <FileCheck className="h-4 w-4 text-emerald-500" />,
  atribut,
});
const ROW_SARPRAS = (
  nama: string,
  status: string,
  atribut: string,
): DokumenRow => ({
  kategori: "SARPRAS",
  icon: <Laptop className="h-4 w-4 text-rose-600" />,
  chip: "bg-rose-50 text-rose-700",
  nama,
  status,
  statusIcon: <FileEdit className="h-4 w-4 text-sky-500" />,
  atribut,
});

const DATASETS: Record<string, Dataset> = {
  "MIGAS 2026": {
    title: "PNBP SDA MINYAK & GAS BUMI",
    anchorLaw: "UU No. 22/2001 & PP No. 35/2004",
    picUtama: "Bambang Hermawanto",
    picRole: "Direktur",
    progress: 92,
    rows: [
      ROW_REGULASI("PP No. 35/2004", "Valid", "Bagi Hasil Migas"),
      ROW_PROSES("SOP Lifting Migas", "Final", "Alur Penagihan FTP"),
      ROW_TATA("SK Tim PNBP Migas", "Final", "Koordinator: Rina Sari"),
      ROW_SARPRAS("Server Data Lifting", "Valid", "Storage 50TB & Backup"),
    ],
    insights: [
      {
        icon: <AlertTriangle className="h-5 w-5 text-rose-500" />,
        title: "Alert Lifting",
        body: (
          <>
            Data lifting Q4 2025 memiliki selisih{" "}
            <span className="font-semibold text-rose-600">2.3%</span> dibanding
            laporan KKKS, butuh rekonsiliasi.
          </>
        ),
      },
      {
        icon: <UsersIcon className="h-5 w-5 text-amber-500" />,
        title: "Analisis Beban",
        body: <>Tim Verifikasi Migas mengelola 38 KKKS aktif di 2026.</>,
      },
      {
        icon: <TrendingUp className="h-5 w-5 text-sky-500" />,
        title: "Proyeksi Penerimaan",
        body: <>Estimasi PNBP Migas 2026 naik 8% dengan kenaikan ICP.</>,
      },
    ],
  },
  "PABUM 2026": {
    title: "PNBP SDA PANAS BUMI & KND",
    anchorLaw: "UU No. 21/2014 & PP No. 7/2017",
    picUtama: "Wawan Sunarjo",
    picRole: "Dirjen",
    progress: 85,
    rows: [
      ROW_REGULASI("PMK No. 123/2024", "Valid", "Dasar Ad Valorem"),
      ROW_PROSES("SOP Verifikasi Setoran", "Review", "Alur Penentuan Terutang"),
      ROW_TATA("SK Panitia Rakorgab", "Final", "Koordinator: Dyah Kusumawati"),
      ROW_SARPRAS(
        "Kertas Kerja TIK & Sarpras",
        "Draft",
        "Laptop & Lisensi Canva",
      ),
    ],
    insights: [
      {
        icon: <AlertTriangle className="h-5 w-5 text-rose-500" />,
        title: "Alert Kelengkapan",
        body: (
          <>
            Objek Rakorgab 2025 memiliki urgensi{" "}
            <span className="font-semibold text-rose-600">TINGGI</span>, namun
            dokumen SOP Akomodasi belum tersedia.
          </>
        ),
      },
      {
        icon: <UsersIcon className="h-5 w-5 text-amber-500" />,
        title: "Analisis Beban",
        body: (
          <>
            Tim Kerja Finalisasi Kebijakan memiliki beban ganda (PIC I Wayan)
            pada fungsi Keuangan dan Transportasi.
          </>
        ),
      },
      {
        icon: <TrendingUp className="h-5 w-5 text-sky-500" />,
        title: "Proyeksi Sarpras",
        body: (
          <>
            Kebutuhan Lisensi Nitro PDF (8 unit) meningkat untuk mendukung
            digitalisasi SOP pada TA 2027.
          </>
        ),
      },
    ],
  },
  "KND 2026": {
    title: "PNBP KEKAYAAN NEGARA DIPISAHKAN",
    anchorLaw: "UU No. 19/2003 & PP No. 41/2003",
    picUtama: "Siti Aminah",
    picRole: "Direktur KND",
    progress: 78,
    rows: [
      ROW_REGULASI("PMK No. 88/2023", "Valid", "Dividen BUMN"),
      ROW_PROSES("SOP Setoran Dividen", "Final", "Mekanisme RUPS"),
      ROW_TATA("SK Tim Pengawas KND", "Review", "Koordinator: Andi Wijaya"),
      ROW_SARPRAS("Dashboard Monitoring KND", "Draft", "Lisensi Tableau"),
    ],
    insights: [
      {
        icon: <AlertTriangle className="h-5 w-5 text-rose-500" />,
        title: "Alert Dividen",
        body: <>3 BUMN belum menyetor dividen TA 2025 sesuai jadwal.</>,
      },
      {
        icon: <UsersIcon className="h-5 w-5 text-amber-500" />,
        title: "Analisis Portfolio",
        body: <>Portfolio KND meliputi 41 BUMN dengan total aset Rp 9.000 T.</>,
      },
      {
        icon: <TrendingUp className="h-5 w-5 text-sky-500" />,
        title: "Proyeksi Dividen",
        body: <>Target dividen KND 2026: Rp 85 T (naik 12% YoY).</>,
      },
    ],
  },
  "KESDM 2026": {
    title: "PNBP KEMENTERIAN ESDM",
    anchorLaw: "PP No. 81/2019",
    picUtama: "Rachmat Hidayat",
    picRole: "Sekjen ESDM",
    progress: 70,
    rows: [
      ROW_REGULASI("PP No. 81/2019", "Valid", "Tarif PNBP ESDM"),
      ROW_PROSES("SOP Iuran Tetap Tambang", "Review", "Penagihan Royalti"),
      ROW_TATA("SK Direktorat PNBP ESDM", "Final", "Koordinator: Lestari N."),
      ROW_SARPRAS("Sistem MOMS Mineral", "Valid", "Server & Lisensi GIS"),
    ],
    insights: [
      {
        icon: <AlertTriangle className="h-5 w-5 text-rose-500" />,
        title: "Alert Royalti",
        body: <>Tunggakan royalti minerba Q3 2025 mencapai Rp 1.2 T.</>,
      },
      {
        icon: <UsersIcon className="h-5 w-5 text-amber-500" />,
        title: "Analisis IUP",
        body: <>Total 1.247 IUP aktif diawasi oleh 18 verifikator.</>,
      },
      {
        icon: <TrendingUp className="h-5 w-5 text-sky-500" />,
        title: "Proyeksi Minerba",
        body: <>Penerimaan minerba 2026 diproyeksi Rp 145 T.</>,
      },
    ],
  },
  "KEMENHUT 2026": {
    title: "PNBP KEMENTERIAN KEHUTANAN",
    anchorLaw: "PP No. 12/2014",
    picUtama: "Indra Pratama",
    picRole: "Direktur PNBP Hutan",
    progress: 65,
    rows: [
      ROW_REGULASI("PP No. 12/2014", "Valid", "PSDH & DR"),
      ROW_PROSES("SOP Penagihan PSDH", "Review", "Verifikasi Tebangan"),
      ROW_TATA("SK Tim PNBP Kehutanan", "Final", "Koordinator: Rina H."),
      ROW_SARPRAS("Drone Patroli Hutan", "Draft", "12 unit drone & SDM"),
    ],
    insights: [
      {
        icon: <AlertTriangle className="h-5 w-5 text-rose-500" />,
        title: "Alert Tunggakan",
        body: <>15 IUPHHK menunggak PSDH lebih dari 90 hari.</>,
      },
      {
        icon: <UsersIcon className="h-5 w-5 text-amber-500" />,
        title: "Analisis Lahan",
        body: <>Pengawasan mencakup 28.4 juta Ha hutan produksi.</>,
      },
      {
        icon: <TrendingUp className="h-5 w-5 text-sky-500" />,
        title: "Proyeksi DR",
        body: <>Dana Reboisasi 2026 ditargetkan Rp 4.8 T.</>,
      },
    ],
  },
};

const KATEGORI_OPTIONS = [
  "1. Organisasi",
  "2. Regulasi",
  "3. Pedoman Operasional",
  "4. Rekomendasi Kebijakan",
  "5. Data dan Statistik",
  "6. Sistem Informasi",
  "7. Knowledge Sharing",
];

const PNBP_OPTIONS = Object.keys(DATASETS);

export default function PnbpPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStakeholderId = searchParams.get("stakeholder");
  const [pnbp, setPnbp] = useState<string>(PNBP_OPTIONS[0]);
  const [openPnbp, setOpenPnbp] = useState(false);
  const [selectedStakeholderId, setSelectedStakeholderId] = useState<
    string | null
  >(initialStakeholderId);
  const [selectedDimensiKode, setSelectedDimensiKode] = useState<string | null>(
    null,
  );
  const [previewDoc, setPreviewDoc] = useState<DokumenRowApi | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const data = DATASETS[pnbp];

  // ── Dimensi tab scroll state ───────────────────────────────────────────────
  const tabScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  const scrollTabs = (dir: "left" | "right") => {
    const el = tabScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
  };

  // ── Debounce search input (300ms) ──────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Fetch stakeholder list from public endpoint ────────────────────────────
  const { data: stakeholderList = [] } = useQuery<Stakeholder[]>({
    queryKey: ["public", "stakeholder"],
    queryFn: () => api.get<Stakeholder[]>("/public/stakeholder"),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // null = belum dipilih (tampilkan semua dokumen, tidak filter by stakeholder)
  const selectedStakeholder = selectedStakeholderId
    ? (stakeholderList.find((s) => s.id === selectedStakeholderId) ?? null)
    : null;

  // ── Fetch dimensi (used as tabs) ───────────────────────────────────────────
  const { data: dimensiListRaw = [] } = useQuery<Dimensi[]>({
    queryKey: ["public", "dimensi"],
    queryFn: () => api.get<Dimensi[]>("/public/dimensi"),
    staleTime: 10 * 60 * 1000,
  });

  // Apply display order + label override (DIMENSI_DISPLAY).
  // Tabs not in the map fall to the end, sorted by DB urutan.
  const dimensiList = [...dimensiListRaw]
    .map((d) => ({
      ...d,
      nama: DIMENSI_DISPLAY[d.kode]?.nama ?? d.nama,
      _order: DIMENSI_DISPLAY[d.kode]?.order ?? 1000 + d.urutan,
    }))
    .sort((a, b) => a._order - b._order);

  const activeDimensi =
    dimensiList.find((d) => d.kode === selectedDimensiKode) ?? dimensiList[0];

  // ── Pagination state ──────────────────────────────────────────────────────
  const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
  const [pageSize, setPageSize] = useState<number>(5);
  const [page, setPage] = useState<number>(1); // 1-based

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [activeDimensi?.kode, selectedStakeholder?.id, debouncedSearch, pageSize]);

  // ── Fetch documents filtered by active dimensi ────────────────────────────
  const { data: dokumenPage, isLoading: loadingDokumen } = useQuery<{
    items: DokumenRowApi[];
    total: number;
  }>({
    queryKey: [
      "public",
      "dokumen",
      activeDimensi?.kode,
      selectedStakeholder?.id,
      debouncedSearch,
      page,
      pageSize,
    ],
    queryFn: () =>
      api.get<{ items: DokumenRowApi[]; total: number }>("/public/dokumen", {
        dimensi_kode: activeDimensi?.kode,
        stakeholder_id: selectedStakeholder?.id || undefined,
        q: debouncedSearch || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    enabled: !!activeDimensi,
    staleTime: 60 * 1000,
  });

  const dokumenList: DokumenRowApi[] = dokumenPage?.items ?? [];
  const totalDokumen = dokumenPage?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalDokumen / pageSize));
  const firstRow = totalDokumen === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, totalDokumen);

  return (
    <div className="min-h-screen bg-white text-base text-slate-900 antialiased">
      <PublicHeader />
      <div className="mx-auto max-w-7xl divide-y divide-slate-200/60 px-6 py-12 [&>section]:py-12 [&>section:first-child]:pt-0 [&>section:last-child]:pb-0">
        {/* ── Top section (header bar + hero + tabs) ──────────────── */}
        <section className="overflow-hidden">
          {/* Top bar: JENIS PNBP dropdown (left)  +  Export (right) */}
          <div className="flex items-center justify-between py-4">
            <div className="relative">
              <button
                onClick={() => setOpenPnbp((v) => !v)}
                className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: "#1B5189" }}
              >
                {selectedStakeholder
                  ? selectedStakeholder.nama.toUpperCase()
                  : "Stakeholder PNBP"}
                <ChevronDown className="h-4 w-4" />
              </button>
              {openPnbp && stakeholderList.length > 0 && (
                <ul className="absolute left-0 top-full z-20 mt-1 max-h-72 w-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {stakeholderList.map((s) => {
                    const isActive = s.id === selectedStakeholder?.id;
                    return (
                      <li key={s.id}>
                        <button
                          onClick={() => {
                            setSelectedStakeholderId(s.id);
                            setSearchParams(
                              (prev) => {
                                const next = new URLSearchParams(prev);
                                next.set("stakeholder", s.id);
                                return next;
                              },
                              { replace: true },
                            );
                            setOpenPnbp(false);
                          }}
                          className={`block w-full px-4 py-2 text-left text-sm uppercase hover:bg-slate-50 ${
                            isActive
                              ? "bg-indigo-50 font-semibold text-indigo-700"
                              : "text-slate-700"
                          }`}
                        >
                          {s.nama}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* <button
              className="flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              style={{ backgroundColor: "#1B2A4E" }}
            >
              <Download className="h-4 w-4" /> export
            </button> */}
          </div>

          {/* Hero: gradient cyan → navy with curved bottom */}
          <div className="pb-10 pt-2">
            <div
              className="relative overflow-hidden rounded-3xl px-8 py-16 text-center text-white shadow-xl"
              style={{
                background:
                  "linear-gradient(135deg, #2DD4BF 0%, #1B5189 50%, #0F2547 100%)",
              }}
            >
              <h1 className="text-3xl font-bold tracking-wide sm:text-4xl">
                PNBP{" "}
                {selectedStakeholder
                  ? selectedStakeholder.nama.toUpperCase()
                  : ""}
              </h1>
              {/* <div className="mt-6 flex items-center justify-center gap-2 text-base">
                <MapPin className="h-5 w-5 text-cyan-200" />
                <span className="font-semibold">Anchor Law:</span>
                <span className="text-white/90">{data.anchorLaw}</span>
              </div>
              <div className="mt-2 flex items-center justify-center gap-2 text-base">
                <User className="h-5 w-5 text-cyan-200" />
                <span className="font-semibold">PIC Utama:</span>
                <span className="text-white/90">
                  {data.picUtama}{" "}
                  <span className="text-white/70">({data.picRole})</span>
                </span>
              </div> */}
            </div>
          </div>

          {/* Tabs + Table — wrapped in a soft-bordered container */}
          <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white">
            {/* Tabs row — driven by Dimensi (functional dimensions) */}
            <div className="relative border-b border-slate-200/70">
              {/* Left arrow */}
              {canScrollLeft && (
                <button
                  onClick={() => scrollTabs("left")}
                  className="absolute left-0 top-0 z-10 flex h-full items-center bg-gradient-to-r from-white via-white/90 to-transparent pl-1 pr-3 text-slate-400 hover:text-slate-700"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}

              <div
                ref={tabScrollRef}
                className="overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                <nav className="flex w-full items-center" aria-label="Tabs">
                  {dimensiList.map((d) => {
                    const isActive = d.kode === activeDimensi?.kode;
                    return (
                      <button
                        key={d.id}
                        title={d.nama}
                        onClick={() => setSelectedDimensiKode(d.kode)}
                        className={`relative -mb-px min-w-[calc(100%/6)] flex-none truncate border-b-2 px-3 py-3 text-center text-[13px] transition ${
                          isActive
                            ? "font-semibold"
                            : "border-transparent font-normal text-slate-600 hover:text-slate-900"
                        }`}
                        style={
                          isActive
                            ? { color: "#1B5189", borderColor: "#1B5189" }
                            : undefined
                        }
                      >
                        {d.nama}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Right arrow */}
              {canScrollRight && (
                <button
                  onClick={() => scrollTabs("right")}
                  className="absolute right-0 top-0 z-10 flex h-full items-center bg-gradient-to-l from-white via-white/90 to-transparent pl-3 pr-1 text-slate-400 hover:text-slate-700"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Toolbar: result count (left) + search input (right) */}
            <div className="flex flex-col gap-3 border-b border-slate-200/70 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-slate-500">
                {totalDokumen === 0 ? (
                  <>Belum ada dokumen</>
                ) : (
                  <>
                    Menampilkan{" "}
                    <span className="font-semibold text-slate-700">
                      {firstRow}-{lastRow}
                    </span>{" "}
                    dari{" "}
                    <span className="font-semibold text-slate-700">
                      {totalDokumen}
                    </span>{" "}
                    dokumen
                  </>
                )}
                {activeDimensi && (
                  <>
                    {" "}
                    untuk{" "}
                    <span className="font-medium text-slate-700">
                      {activeDimensi.nama}
                    </span>
                  </>
                )}
                {debouncedSearch && (
                  <>
                    {" "}
                    &middot; pencarian &ldquo;
                    <span className="font-medium text-slate-700">
                      {debouncedSearch}
                    </span>
                    &rdquo;
                  </>
                )}
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cari dokumen…"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </div>
            </div>

            {/* Content table — documents filtered by active dimensi */}
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <th scope="col" className="w-44 px-6 py-3">
                      Kategori
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Nama Dokumen
                    </th>
                    <th scope="col" className="w-40 px-6 py-3">
                      Status
                    </th>
                    <th scope="col" className="w-72 px-6 py-3">
                      Atribut Penting
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {loadingDokumen && (
                    <tr>
                      <td colSpan={4} className="px-6 py-16">
                        <div className="flex flex-col items-center justify-center gap-3 text-slate-400">
                          <Loader2 className="h-6 w-6 animate-spin" />
                          <span className="text-sm font-medium">
                            Memuat dokumen…
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loadingDokumen && dokumenList.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-20">
                        <div className="mx-auto flex max-w-sm flex-col items-center justify-center gap-4 text-center">
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                            <Inbox className="h-8 w-8 text-slate-400" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-slate-700">
                              Belum ada dokumen
                            </h3>
                            <p className="mt-1 text-sm text-slate-500">
                              Belum ada dokumen yang tersedia untuk dimensi{" "}
                              <span className="font-medium text-slate-700">
                                {activeDimensi?.nama ?? "ini"}
                              </span>
                              . Coba pilih dimensi lain di tab di atas.
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loadingDokumen &&
                    dokumenList.map((row) => {
                      const visibleTags = row.tags.slice(0, 3);
                      const hiddenCount = row.tags.length - visibleTags.length;
                      return (
                        <tr
                          key={row.id}
                          onClick={() => setPreviewDoc(row)}
                          className="cursor-pointer hover:bg-slate-50/50"
                        >
                          <td className="px-6 py-4">
                            {row.kategori ? (
                              <span className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-1.5 text-xs font-bold uppercase text-slate-700">
                                {row.kategori}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-slate-700">
                            {row.judul}
                            {row.nomor_dokumen && (
                              <div className="mt-0.5 text-xs text-slate-400">
                                {row.nomor_dokumen}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="px-6 py-4">
                            {row.tags.length === 0 ? (
                              <span className="text-xs text-slate-400">—</span>
                            ) : (
                              <div className="flex flex-wrap items-center gap-1.5">
                                {visibleTags.map((t) => (
                                  <span
                                    key={t}
                                    className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                                  >
                                    {t}
                                  </span>
                                ))}
                                {hiddenCount > 0 && (
                                  <span
                                    className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"
                                    title={row.tags.slice(3).join(", ")}
                                  >
                                    +{hiddenCount}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Pagination bar */}
            {totalDokumen > 0 && (
              <div className="flex flex-col gap-3 border-t border-slate-200/70 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>Baris per halaman</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">
                    Halaman{" "}
                    <span className="font-semibold text-slate-700">{page}</span>{" "}
                    dari{" "}
                    <span className="font-semibold text-slate-700">
                      {totalPages}
                    </span>
                  </span>
                  <div className="ml-2 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || loadingDokumen}
                      className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Prev
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page >= totalPages || loadingDokumen}
                      className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── Knowledge Orbit ─────────────────────────────────────── */}
        {/* <section>
          <h2 className="text-center text-3xl font-bold text-slate-900">
            Knowledge Orbit
          </h2>

          <KnowledgeOrbit />

          <p className="mt-6 text-center text-sm uppercase tracking-widest text-slate-400">
            Relasi: Konten
          </p>
        </section> */}

        {/* ── Smart Insight ───────────────────────────────────────── */}
        {/* <section>
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-rose-500" />
            <h2 className="text-2xl font-bold text-slate-900">Smart Insight</h2>
          </div>
          <p className="text-sm text-slate-400">AI-Generated Analysis</p>

          <div className="mt-6 space-y-4">
            {data.insights.map((i) => (
              <div
                key={i.title}
                className="flex gap-4 rounded-xl border border-slate-100 bg-slate-50/60 p-5"
              >
                <div className="mt-0.5">{i.icon}</div>
                <div>
                  <div className="text-base font-semibold text-slate-800">
                    {i.title}
                  </div>
                  <div className="mt-1 text-base leading-relaxed text-slate-600">
                    {i.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section> */}
      </div>

      {/* ── Document preview modal ───────────────────────────────── */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

// ── Document preview modal ───────────────────────────────────────────────────

function formatBytes(bytes: number | null): string {
  if (!bytes && bytes !== 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1";

// Renders the actual document content based on its mime type.
// PDFs use a native browser <iframe>, images use <img>, anything else
// falls back to a download link.
function DocumentFileViewer({ doc }: { doc: DokumenRowApi }) {
  if (!doc.file_name) {
    return (
      <div className="mb-6 flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-sm text-slate-500">
        Dokumen ini belum memiliki file terlampir.
      </div>
    );
  }

  const url = `${API_BASE}/public/dokumen/${doc.id}/preview`;
  const mime = (doc.mime_type ?? "").toLowerCase();

  // PDF viewer (most common case)
  if (
    mime === "application/pdf" ||
    doc.file_name.toLowerCase().endsWith(".pdf")
  ) {
    return (
      <div className="mb-6 overflow-hidden rounded-xl border border-slate-200">
        <iframe
          src={url}
          title={doc.file_name}
          className="h-[70vh] w-full bg-slate-100"
        />
      </div>
    );
  }

  // Image viewer
  if (mime.startsWith("image/")) {
    return (
      <div className="mb-6 flex items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-4">
        <img
          src={url}
          alt={doc.file_name}
          className="max-h-[70vh] w-auto object-contain"
        />
      </div>
    );
  }

  // Plain text / markdown / json — embed via iframe (browser will render as text)
  if (
    mime.startsWith("text/") ||
    mime === "application/json" ||
    /\.(txt|md|json|csv|log)$/i.test(doc.file_name)
  ) {
    return (
      <div className="mb-6 overflow-hidden rounded-xl border border-slate-200">
        <iframe
          src={url}
          title={doc.file_name}
          className="h-[60vh] w-full bg-white"
        />
      </div>
    );
  }

  // Office docs / unknown — show fallback with a "Buka di tab baru" button
  return (
    <div className="mb-6 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
      <FileText className="mx-auto h-10 w-10 text-slate-400" />
      <p className="mt-3 text-sm text-slate-600">
        Preview inline tidak tersedia untuk tipe file ini
        {doc.mime_type && (
          <span className="ml-1 font-mono text-xs text-slate-500">
            ({doc.mime_type})
          </span>
        )}
        .
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
        style={{ backgroundColor: "#1B5189" }}
      >
        <Download className="h-4 w-4" />
        Buka / Unduh File
      </a>
    </div>
  );
}

function DocumentPreviewModal({
  doc,
  onClose,
}: {
  doc: DokumenRowApi;
  onClose: () => void;
}) {
  const tanggal = doc.tanggal_dokumen
    ? new Date(doc.tanggal_dokumen).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-8 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between gap-4 px-6 py-5 text-white"
          style={{ backgroundColor: "#1B5189" }}
        >
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-white/15 p-2">
              <FileText className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-white/70">
                {doc.kategori ?? "Dokumen"}
              </div>
              <h3 className="mt-0.5 text-xl font-bold leading-tight">
                {doc.judul}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-white/80 transition hover:bg-white/10 hover:text-white"
            aria-label="Tutup"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[calc(90vh-5rem)] overflow-y-auto px-6 py-6">
          {/* File viewer (real document content) */}
          <DocumentFileViewer doc={doc} />

          {/* Meta grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {doc.nomor_dokumen && (
              <div className="flex items-start gap-2">
                <Hash className="mt-0.5 h-4 w-4 text-slate-400" />
                <div>
                  <div className="text-xs font-medium uppercase text-slate-500">
                    Nomor Dokumen
                  </div>
                  <div className="text-sm text-slate-800">
                    {doc.nomor_dokumen}
                  </div>
                </div>
              </div>
            )}
            {tanggal && (
              <div className="flex items-start gap-2">
                <Calendar className="mt-0.5 h-4 w-4 text-slate-400" />
                <div>
                  <div className="text-xs font-medium uppercase text-slate-500">
                    Tanggal Dokumen
                  </div>
                  <div className="text-sm text-slate-800">{tanggal}</div>
                </div>
              </div>
            )}
            <div className="flex items-start gap-2">
              <FileCheck className="mt-0.5 h-4 w-4 text-slate-400" />
              <div>
                <div className="text-xs font-medium uppercase text-slate-500">
                  Status
                </div>
                <div className="mt-0.5">
                  <StatusBadge status={doc.status} />
                </div>
              </div>
            </div>
            {doc.file_name && (
              <div className="flex items-start gap-2">
                <FileText className="mt-0.5 h-4 w-4 text-slate-400" />
                <div>
                  <div className="text-xs font-medium uppercase text-slate-500">
                    File
                  </div>
                  <div className="text-sm text-slate-800">{doc.file_name}</div>
                  <div className="text-xs text-slate-400">
                    {formatBytes(doc.file_size)}
                    {doc.mime_type && ` · ${doc.mime_type}`}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {doc.deskripsi && (
            <div className="mt-6 border-t border-slate-100 pt-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Deskripsi
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {doc.deskripsi}
              </p>
            </div>
          )}

          {/* AI Summary */}
          {doc.ai_summary && (
            <div className="mt-6 border-t border-slate-100 pt-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                AI Summary
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {doc.ai_summary}
              </p>
            </div>
          )}

          {/* Tags */}
          {doc.tags.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Atribut Penting
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {doc.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// @ts-ignore -- commented out with call site above
function KnowledgeOrbit() {
  return (
    <div className="relative mx-auto mt-8 h-[640px] w-full max-w-5xl text-base">
      {/* ── Corner quadrant lists ─────────────────────────── */}
      <div className="absolute left-0 top-0 w-64">
        <div className="text-sm font-bold uppercase tracking-wide text-amber-600">
          Kuadran 1: Regulasi
        </div>
        <ul className="mt-2 space-y-2 text-sm text-slate-700">
          <li className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded border border-amber-300 bg-amber-50 text-amber-600">
              ✓
            </span>
            UU 21/2014
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded border border-amber-300 bg-amber-50 text-amber-600">
              ✓
            </span>
            PMK 123/2024
          </li>
        </ul>
      </div>

      <div className="absolute right-0 top-0 w-64 text-right">
        <div className="text-sm font-bold uppercase tracking-wide text-violet-600">
          Kuadran 2: Manusia
        </div>
        <ul className="mt-2 space-y-3 text-sm text-slate-700">
          <li className="flex items-center justify-end gap-2">
            Wawan Sunarjo <span className="text-slate-400">(PJ)</span>
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-100 ring-2 ring-white">
              <User className="h-4 w-4 text-violet-600" />
            </span>
          </li>
          <li className="flex items-center justify-end gap-2">
            Dyah Kusumawati <span className="text-slate-400">(PIC)</span>
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-pink-100 ring-2 ring-white">
              <User className="h-4 w-4 text-pink-600" />
            </span>
          </li>
        </ul>
      </div>

      <div className="absolute bottom-0 left-0 w-64">
        <div className="text-sm font-bold uppercase tracking-wide text-sky-600">
          Kuadran 3: Proses
        </div>
        <ul className="mt-2 space-y-2 text-sm text-slate-700">
          <li className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded border border-sky-300 bg-sky-50 text-sky-600">
              ✓
            </span>
            SOP Verifikasi Setoran
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded border border-sky-300 bg-sky-50 text-sky-600">
              ✓
            </span>
            Roadmap PNBP 2025
          </li>
        </ul>
      </div>

      <div className="absolute bottom-0 right-0 w-64 text-right">
        <div className="text-sm font-bold uppercase tracking-wide text-rose-600">
          Kuadran 4: Sarpras
        </div>
        <ul className="mt-2 space-y-3 text-sm text-slate-700">
          <li className="flex items-center justify-end gap-2">
            Laptop Editing <span className="text-slate-400">(Fisik)</span>
            <LaptopIcon className="h-7 w-7" />
          </li>
          <li className="flex items-center justify-end gap-2">
            Lisensi Canva <span className="text-slate-400">(Digital)</span>
            <CanvaIcon className="h-7 w-7" />
          </li>
        </ul>
      </div>

      {/* ── Center orbit (SVG) ─────────────────────────── */}
      <div className="absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2">
        <svg viewBox="0 0 440 440" className="h-full w-full">
          <defs>
            <radialGradient id="bgRing" cx="50%" cy="50%" r="50%">
              <stop offset="60%" stopColor="#fff" />
              <stop offset="100%" stopColor="#f5f3ff" />
            </radialGradient>
            <linearGradient id="quadAmber" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fef3c7" />
              <stop offset="100%" stopColor="#fde68a" stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="quadViolet" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ede9fe" />
              <stop offset="100%" stopColor="#ddd6fe" stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="quadSky" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e0f2fe" />
              <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="quadRose" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffe4e6" />
              <stop offset="100%" stopColor="#fecdd3" stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="centerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
            <marker
              id="arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="5"
              markerHeight="5"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 z" fill="#cbd5e1" />
            </marker>
          </defs>

          {/* Outer ring */}
          <circle cx="220" cy="220" r="200" fill="url(#bgRing)" />

          {/* Quadrant sectors */}
          <path
            d="M220,220 L220,20 A200,200 0 0,0 20,220 Z"
            fill="url(#quadAmber)"
          />
          <path
            d="M220,220 L420,220 A200,200 0 0,0 220,20 Z"
            fill="url(#quadViolet)"
          />
          <path
            d="M220,220 L20,220 A200,200 0 0,0 220,420 Z"
            fill="url(#quadSky)"
          />
          <path
            d="M220,220 L220,420 A200,200 0 0,0 420,220 Z"
            fill="url(#quadRose)"
          />

          {/* Quadrant labels — centered inside each sector at radius ~115 */}
          <text
            x="135"
            y="115"
            textAnchor="middle"
            fill="#d97706"
            fontSize="15"
            fontWeight="800"
            letterSpacing="0.5"
          >
            REGULASI
          </text>
          <text
            x="305"
            y="115"
            textAnchor="middle"
            fill="#7c3aed"
            fontSize="15"
            fontWeight="800"
            letterSpacing="0.5"
          >
            MANUSIA
          </text>
          <text
            x="135"
            y="335"
            textAnchor="middle"
            fill="#0ea5e9"
            fontSize="14"
            fontWeight="700"
            fontStyle="italic"
          >
            Landasan
          </text>
          <text
            x="305"
            y="335"
            textAnchor="middle"
            fill="#e11d48"
            fontSize="15"
            fontWeight="800"
            letterSpacing="0.5"
          >
            SARPRAS
          </text>

          {/* Short straight connectors from center circle edge to icon edge */}
          <line
            x1="159"
            y1="183"
            x2="110"
            y2="153"
            stroke="#f59e0b"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            markerEnd="url(#arrow)"
          />
          <line
            x1="283"
            y1="185"
            x2="356"
            y2="144"
            stroke="#a78bfa"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            markerEnd="url(#arrow)"
          />
          <line
            x1="292"
            y1="215"
            x2="372"
            y2="210"
            stroke="#ec4899"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            markerEnd="url(#arrow)"
          />
          <line
            x1="160"
            y1="259"
            x2="110"
            y2="292"
            stroke="#0ea5e9"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            markerEnd="url(#arrow)"
          />
          <line
            x1="287"
            y1="247"
            x2="351"
            y2="273"
            stroke="#10b981"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            markerEnd="url(#arrow)"
          />
          <line
            x1="277"
            y1="265"
            x2="352"
            y2="324"
            stroke="#10b981"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            markerEnd="url(#arrow)"
          />

          {/* Center master object */}
          <circle
            cx="220"
            cy="220"
            r="72"
            fill="url(#centerGrad)"
            stroke="#fff"
            strokeWidth="6"
          />
          <text
            x="220"
            y="210"
            textAnchor="middle"
            fill="#fff"
            fontSize="14"
            fontWeight="800"
          >
            SDA
          </text>
          <text
            x="220"
            y="228"
            textAnchor="middle"
            fill="#fff"
            fontSize="14"
            fontWeight="800"
          >
            PANAS BUMI
          </text>
          <text
            x="220"
            y="248"
            textAnchor="middle"
            fill="#dbeafe"
            fontSize="8"
            fontWeight="600"
            letterSpacing="1"
          >
            MASTER OBJECT
          </text>
        </svg>

        {/* Floating icon nodes (HTML overlay for richer styling) */}
        {/* Top-left: Regulasi document tile */}
        <span className="absolute left-[18%] top-[31%] flex h-10 w-10 items-center justify-center rounded-lg border border-amber-200 bg-white shadow-md">
          <Scale className="h-5 w-5 text-amber-600" />
        </span>
        {/* Top-right: PJ avatar */}
        <span className="absolute right-[12%] top-[29%] flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 shadow-md ring-2 ring-white">
          <User className="h-5 w-5 text-violet-600" />
        </span>
        <span className="absolute right-[10%] top-[31%] rounded-full bg-violet-500 px-1.5 py-px text-[9px] font-bold text-white shadow">
          PJ
        </span>
        {/* Right middle: PIC avatar */}
        <span className="absolute right-[8%] top-[44%] flex h-10 w-10 items-center justify-center rounded-full bg-pink-100 shadow-md ring-2 ring-white">
          <User className="h-5 w-5 text-pink-600" />
        </span>
        <span className="absolute right-[6%] top-[46%] rounded-full bg-pink-500 px-1.5 py-px text-[9px] font-bold text-white shadow">
          PC
        </span>
        {/* Bottom-left: SOP doc */}
        <span className="absolute left-[18%] top-[63%] flex h-10 w-10 items-center justify-center rounded-lg border border-sky-200 bg-white shadow-md">
          <FileEdit className="h-5 w-5 text-sky-600" />
        </span>
        {/* Bottom-right: Laptop tile */}
        <span className="absolute right-[12%] top-[59%] flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-md">
          <LaptopIcon className="h-5 w-5" />
        </span>
        {/* Bottom-right: Canva tile */}
        <span className="absolute right-[12%] top-[70%] flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-md">
          <CanvaIcon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

// ── Brand-styled icons ───────────────────────────────────────────────────────

// @ts-ignore -- commented out with call site above
function CanvaIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-label="Canva">
      <defs>
        <linearGradient id="canvaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00C4CC" />
          <stop offset="50%" stopColor="#7D2AE7" />
          <stop offset="100%" stopColor="#00C4CC" />
        </linearGradient>
      </defs>
      <circle cx="16" cy="16" r="16" fill="url(#canvaGrad)" />
      <path
        d="M21.6 19.4c-.2 0-.4.1-.6.4-1.1 1.6-2.6 2.6-4.3 2.6-2.7 0-4.4-2.4-4.4-5.6 0-3.6 2-6.5 4.5-6.5 1.2 0 1.9.7 1.9 1.6 0 .6-.3 1.1-.7 1.6-.3.3-.5.5-.5.8 0 .5.5.9 1.2.9 1.4 0 2.5-1.3 2.5-3 0-2-1.8-3.5-4.5-3.5-4.4 0-7.7 3.9-7.7 8.5 0 4.4 2.9 7.4 6.9 7.4 3 0 5.4-1.9 6.2-4.4.1-.4-.2-.8-.5-.8z"
        fill="#fff"
      />
    </svg>
  );
}

// @ts-ignore -- commented out with call site above
function LaptopIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 24" className={className} aria-label="Laptop">
      <defs>
        <linearGradient id="laptopGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>
      </defs>
      <rect
        x="5"
        y="3"
        width="22"
        height="14"
        rx="1.5"
        fill="url(#laptopGrad)"
        stroke="#334155"
        strokeWidth="0.5"
      />
      <rect x="6.5" y="4.5" width="19" height="11" rx="0.5" fill="#e0f2fe" />
      <path
        d="M10 9 l3 3 5-5 4 4"
        stroke="#0ea5e9"
        strokeWidth="1"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="20" cy="7.5" r="1" fill="#fbbf24" />
      <path
        d="M2 18 h28 l-2 3 H4 z"
        fill="#cbd5e1"
        stroke="#334155"
        strokeWidth="0.5"
      />
      <rect x="14" y="18.5" width="4" height="1" rx="0.5" fill="#94a3b8" />
    </svg>
  );
}
